<script setup lang="ts">
import { ref, type PropType } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';
import { DateTime } from 'luxon';
import { type FormField, type GeolocationPosition, type PositionErrorCallback, LoadingStateModel } from '@/models';
import { ButtonType, CustomButton } from '@/components';
import { formattedDate, DATE_FORMAT_1 } from '@/utils';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  previewMode: {
    type: Boolean
  }
});

const localField = ref<FormField>(props.field);
const retrievingLocation = ref(new LoadingStateModel({isLoading: false}));

const getGPSLocation = () => {
  const success = (position: GeolocationPosition) => {
    localField.value.locationValue = {
      timestamp: position.timestamp / 1000,
      coords: position.coords
    };
    retrievingLocation.value.stopLoadingAndFinish();
  };
  const errorCallback = (error: PositionErrorCallback) => {
    retrievingLocation.value.setError(error);
    retrievingLocation.value.stopLoadingAndFinish();
  };
  localField.value.locationValue = undefined;
  retrievingLocation.value.startLoading();
  //@ts-expect-error Error import issue, can safely ignore
  navigator.geolocation.getCurrentPosition(success, errorCallback);
};

const onResetLocation = () => {
  localField.value.locationValue = undefined;
};
</script>

<template>
    <div class="item-root">
        <div class="location-details" v-if="localField.locationValue?.timestamp">
            <p>
                {{$t('date')}}: {{ formattedDate(DateTime.fromSeconds(localField.locationValue!.timestamp).toJSDate(), DATE_FORMAT_1) }} <br />
                {{$t('latitude')}}: {{ localField.locationValue!.coords.latitude }} <br />
                {{$t('longitude')}}: {{ localField.locationValue!.coords.longitude }} <br />
                {{$t('accuracy')}}: {{ localField.locationValue!.coords.accuracy }}
            </p>
        </div>
        <div v-if="retrievingLocation.isLoading" class="progress-container">
            <ProgressSpinner strokeWidth="4" class="progress"/>
        </div>
        <CustomButton :text="localField.locationValue ? $t('refresh_gps') : $t('load_gps')"
          @onClick="getGPSLocation"
          :disabled="previewMode"
          :type="ButtonType.OUTLINED"/>
        <CustomButton :text="$t('reset')"
          @onClick="onResetLocation"
          class="reset-button"
          v-if="localField.locationValue && !previewMode"
          :type="ButtonType.OUTLINED"/>
    </div>
</template>

<style scoped lang="scss">
.location-details {
  padding-bottom: 16px;
}
.reset-button {
  margin-top: 16px;
}
.progress-container {
  margin-top: 16px;
  margin-bottom: 16px;
}
.progress {
  width: 32px;
  height: 32px;
}
</style>
